import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import * as Yup from "yup";
import { ProductLayout } from "./ProductLayout";
import { productFormConfig } from "../form/productFormConfig";
import { usePanel } from "hooks/usePanel";
import { GenericLoading } from "components/ui/Loader";
import { ValidationTracker } from "components/domain/ValidationTracker/ValidationTracker";
import { filesAPI, productsAPI } from "services/swagger-api";
import { ProductFormValues } from "./productFormValues";
import { toast } from "react-toastify";
import { CreateProductCommand, OperationResponse, ProductDto, TargetedSubscriberEnum, UnitEnum, UpdateProductCommand } from "api/product";
import { parseApiError } from "shared/utils/apiValidationErrors";
import { FileTypeEnum } from "api/actor";
import { ReferentialKey } from "api/referential";
import { useAppSelector } from "app/hooks";
import { selectReferentialData } from "features/referentials/selectors/selectReferentialDataByKey";
import { ProductFormFieldNames } from "./productFormFieldNames";
import { useBranchesCapacites } from "./useBranchesCapacites";

const SUBMIT_ERROR_MESSAGE = "L'enregistrement du produit a échoué";
const LOGO_REQUIRED_MESSAGE = "Le logo du produit est requis";
const UNKNOWN_REFERENTIAL_MESSAGE =
  "Branche ou plateforme inconnue du référentiel : rechargez la page avant de réessayer";

interface ProductFormProps {
  productId?: string;
  onSuccess?: () => void;
}

export const ProductForm: React.FC<ProductFormProps> = ({
  productId,
  onSuccess,
}) => {
  const formikRef = useRef<FormikProps<ProductFormValues>>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [product, setProduct] = useState<ProductDto | null>()
  const { setPanelActions, closePanel } = usePanel();
  const branchesCapacites = useBranchesCapacites();

  useEffect(() => {
    const getProductByID = async (productId: string) => {
      const response = await productsAPI.getProductByID({ productId })
      setProduct(response)
    }
    if (productId) {
      getProductByID(productId)
    }
  }, [productId])

  /* Registered unconditionally: the panel owns the submit and cancel buttons, so
     a ref that is not populated yet must not leave the panel without actions.
     The ref is only read when the button is pressed. */
  useEffect(() => {
    setPanelActions({
      closeLabel: "COMMON.CANCEL",
      submitLabel: "COMMON.VALIDATE",
      submitDisabled: !isFormValid || isLoading,
      onSubmit: () => {
        formikRef.current?.submitForm();
      },
    });
  }, [setPanelActions, isFormValid, isLoading]);

  const referentialsData = useAppSelector(selectReferentialData)

  const getReferentialId = useCallback((referentialKey: ReferentialKey, code?: string) => {
    if (!code) {
      return
    }
    return referentialsData[referentialKey].find(ref => ref.code === code)?.id
  }, [referentialsData])

  const getReferentialKey = useCallback((referentialKey: ReferentialKey, id?: string) => {
    if (!id) {
      return
    }
    return referentialsData[referentialKey].find(ref => ref.id === id)?.code
  }, [referentialsData])


  /* Read direction of the same code <-> id translation: the plateformes of the
     product arrive as ids while the tag widget speaks codes. Ids the referential
     cannot resolve are kept aside instead of vanishing from the form, so saving
     cannot silently unlink them. */
  const editedPlateformes = useMemo(() => {
    const codes: string[] = [];
    const unresolvedIds: string[] = [];
    product?.plateformeIds.forEach(id => {
      const code = getReferentialKey(ReferentialKey.Plateforme, id);
      if (code) {
        codes.push(code);
      } else {
        unresolvedIds.push(id);
      }
    });
    return { codes, unresolvedIds };
  }, [product, getReferentialKey]);

  const onSubmit = async (
    values: ProductFormValues,
    helpers: FormikHelpers<ProductFormValues>
  ) => {
    /* The widgets hold referential CODES while the commands expect IDS. The
       translation must be lossless: a code the referential does not know is
       reported to the user instead of being dropped from the list or sent as-is
       in place of an id. It runs before the logo upload so nothing is uploaded
       for a request that will not be sent. */
    const branchId = getReferentialId(ReferentialKey.Branch, values.branchId);
    const plateformeIds: string[] = [];
    const unknownPlateformeCodes: string[] = [];
    values.plateformeIds.forEach(code => {
      const id = getReferentialId(ReferentialKey.Plateforme, code);
      if (id) {
        plateformeIds.push(id);
      } else {
        unknownPlateformeCodes.push(code);
      }
    });

    if (!branchId) {
      helpers.setFieldTouched(ProductFormFieldNames.BranchId, true, false);
      helpers.setFieldError(ProductFormFieldNames.BranchId, UNKNOWN_REFERENTIAL_MESSAGE);
    }
    if (unknownPlateformeCodes.length > 0) {
      helpers.setFieldTouched(ProductFormFieldNames.PlateformeIds, true, false);
      helpers.setFieldError(ProductFormFieldNames.PlateformeIds, UNKNOWN_REFERENTIAL_MESSAGE);
    }
    if (!branchId || unknownPlateformeCodes.length > 0 || editedPlateformes.unresolvedIds.length > 0) {
      toast.error(UNKNOWN_REFERENTIAL_MESSAGE);
      return;
    }

    setIsLoading(true);
    try {
      // A new file is uploaded only when the user picked one: on update, leaving
      // storedFileId undefined tells the back the logo is unchanged.
      let storedFileId: string | undefined;
      if (values.logo) {
        const fileResponse = await filesAPI.uploadFileByType({
          file: values.logo,
          fileType: FileTypeEnum.LogoProduit
        });
        storedFileId = fileResponse.id || undefined;
      }

      const commonFields = {
        label: values.label,
        summary: values.summary,
        fullDescription: values.fullDescription,
        storedFileId,
        branchId,
        personId: values.personId,
        isTacitRenewal: values.isTacitRenewal,
        isFixedFormula: values.isFixedFormula,
        targetedSubscriber: values.targetedSubscriber,
        noticePeriodDuration: {
          value: values.noticePeriodDuration.value || 0,
          unit: Number(values.noticePeriodDuration.unit) as UnitEnum
        },
        plateformeIds,
      };

      let response: OperationResponse;
      if (productId) {
        // isActive / isSepaActivated are deliberately omitted: the form does not
        // expose them, and the back keeps the current values when they are absent.
        const updateProductCommand: UpdateProductCommand = commonFields;
        response = await productsAPI.updateProduct({
          productId,
          updateProductCommand,
        });
      } else {
        // The logo is required at creation (validation rule and back alike).
        if (!storedFileId) {
          helpers.setFieldTouched(ProductFormFieldNames.Logo, true, false);
          helpers.setFieldError(ProductFormFieldNames.Logo, LOGO_REQUIRED_MESSAGE);
          return;
        }
        // isSepaActivated is omitted too: the back defaults it to true at creation.
        const createProductCommand: CreateProductCommand = {
          ...commonFields,
          storedFileId,
          code: values.code,
        };
        response = await productsAPI.createProduct({
          createProductCommand,
        });
      }

      if (!response.isSuccess) {
        // Business failure comes back with HTTP 200: keep the panel open so the
        // user can correct the form instead of silently losing the input.
        toast.error(response.errorMessage || SUBMIT_ERROR_MESSAGE);
        return;
      }

      onSuccess?.();
      closePanel();
    } catch (error) {
      const parsed = await parseApiError(error);
      toast.error(parsed.message || SUBMIT_ERROR_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  };

  const initialValues = useMemo((): ProductFormValues => {
    if (product) {
      return {
        [ProductFormFieldNames.Label]: product.label,
        [ProductFormFieldNames.Code]: product.code,
        [ProductFormFieldNames.Summary]: product.summary,
        [ProductFormFieldNames.FullDescription]: product.fullDescription,
        // The logo is not re-uploaded on edit: the stored one is only previewed.
        [ProductFormFieldNames.Logo]: undefined,
        [ProductFormFieldNames.BranchId]: getReferentialKey(ReferentialKey.Branch, product.branchId) || product.branchId,
        [ProductFormFieldNames.PersonId]: product.ownerId ?? '',
        [ProductFormFieldNames.IsTacitRenewal]: product.isTacitRenewal,
        [ProductFormFieldNames.IsFixedFormula]: product.isFixedFormula,
        [ProductFormFieldNames.TargetedSubscriber]: product.targetedSubscriber ?? TargetedSubscriberEnum.Any,
        [ProductFormFieldNames.PlateformeIds]: editedPlateformes.codes,
        // Both halves must be seeded, otherwise the notice period rule fails and
        // the panel opens on an already invalid form.
        [ProductFormFieldNames.NoticePeriodDuration]: {
          value: product.noticePeriodDuration.value ?? null,
          unit: product.noticePeriodDuration.unit ?? UnitEnum.Day
        }
      }
    }
    return productFormConfig.initialValues
  }, [product, getReferentialKey, editedPlateformes])

  const validationSchema = useMemo(() => {
    const baseSchema = productFormConfig.validationSchema;

    if (productId) {
      // The stored logo (previewed by ProductLayout) is kept when no new file is
      // picked: a new one is required only when the product has none.
      const editSchema = baseSchema.omit([ProductFormFieldNames.Code]); // Retire la validation du champ "code"
      return product?.logoURL
        ? editSchema.shape({ [ProductFormFieldNames.Logo]: Yup.mixed<File>().notRequired() })
        : editSchema;
    }

    return baseSchema;
  }, [productId, product?.logoURL]);

  return (
    <div className="relative">
      {isLoading && <GenericLoading loading isParentSized />}
      <Formik
        key={product ? product.id : 'new'}
        enableReinitialize
        validateOnMount
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        innerRef={formikRef}
      >
        {({ isValid }) => (
          <>
            <ValidationTracker
              isValid={isValid}
              onValidationChange={setIsFormValid}
            />
            <Form>
              <ProductLayout
                isEdit={!!productId}
                logoUrl={product?.logoURL}
                ownerName={product?.ownerName}
                branchesCapacites={branchesCapacites}
              />
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
};
